import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  LayoutDashboard, Calendar, TrendingUp, Users, DollarSign, Package, Clock, 
  ChevronRight, ArrowUp, ArrowDown, Filter, Crown, Key, Car, Ship, Hotel,
  Briefcase, BookOpen, ShoppingBag, ChevronDown, X, CalendarDays, MapPin, Check
} from 'lucide-react';
import { useI18n } from '../i18n';
import { useApp } from '../context/AppContext';
import { 
  SERVICES, PRODUCTS, COURSES, RENTALS, EVENTS,
  getAllBookingsForOffice, getAllOrdersForOffice,
  getEnabledModulesForOffice, isModuleEnabledForOffice, getEventsByOfficeId,
} from '../data/database';

const DATE_RANGES = [
  { id: 'today', label: 'Oggi' },
  { id: 'week', label: 'Questa settimana' },
  { id: 'month', label: 'Questo mese' },
  { id: 'year', label: 'Quest\'anno' },
  { id: 'custom', label: 'Personalizzato' }
];

const PartnerDashboard = ({ onNavigate }) => {
  const { t } = useI18n();
  const { user, selectedOffice, currentCompany, companyOffices, switchOffice } = useApp();
  
  // Dynamic office data from context
  const officeId = selectedOffice?.id || 1;
  const officeName = selectedOffice?.name || 'La tua attivita';
  const isElitPartner = user?.isElitPartner || selectedOffice?.isElit || false;
  
  // NUOVA LOGICA: Moduli abilitati per questo office
  const enabledModules = useMemo(() => {
    return getEnabledModulesForOffice(officeId);
  }, [officeId]);
  
  const hasModule = (moduleId) => isModuleEnabledForOffice(officeId, moduleId);
  
  // Office switcher state
  const [showOfficeSwitcher, setShowOfficeSwitcher] = useState(false);
  const officeSwitcherRef = useRef(null);
  
  // Close office switcher on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (officeSwitcherRef.current && !officeSwitcherRef.current.contains(e.target)) {
        setShowOfficeSwitcher(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const [dateRange, setDateRange] = useState('week');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showItemFilter, setShowItemFilter] = useState(false);
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  
  // Filter by specific item
  const [filterType, setFilterType] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Get partner's catalog items dynamically
  const partnerServices = useMemo(() => SERVICES.filter(s => s.officeId === officeId), [officeId]);
  const partnerRentals = useMemo(() => (RENTALS || []).filter(r => r.officeId === officeId), [officeId]);
  const partnerProducts = useMemo(() => PRODUCTS.filter(p => p.officeId === officeId), [officeId]);
  const partnerEvents = useMemo(() => getEventsByOfficeId(officeId), [officeId]);
  const partnerCourses = useMemo(() => (COURSES || []).filter(c => c.officeId === officeId), [officeId]);

  // Dynamic data from centralized database
  const officeBookings = useMemo(() => getAllBookingsForOffice(officeId), [officeId]);
  const officeOrders = useMemo(() => getAllOrdersForOffice(officeId), [officeId]);

  // Formatted statistics from database data
  const officeStats = useMemo(() => ({
    bookings: officeBookings.map(b => ({
      id: b.id,
      service: b.serviceName || b.roomType || b.eventType || b.itemName || 'Prenotazione',
      client: b.clientName,
      date: b.date || b.checkIn || b.startDate,
      time: b.time || '',
      price: b.price || b.totalPrice || b.pricePerNight || 0,
      status: b.status,
      isElit: b.isElit,
    })),
    orders: officeOrders.map(o => ({
      id: o.id,
      product: o.items?.[0]?.name || 'Ordine',
      client: o.clientName,
      date: o.date,
      qty: o.items?.reduce((sum, item) => sum + item.qty, 0) || 1,
      price: o.total || 0,
      status: o.status,
    })),
    eventTickets: [],
    courseEnrollments: [],
  }), [officeBookings, officeOrders]);

  // Calculate filtered stats
  const filteredStats = useMemo(() => {
    let bookings = [...officeStats.bookings];
    let orders = [...officeStats.orders];
    let tickets = [...officeStats.eventTickets];
    let enrollments = [...officeStats.courseEnrollments];

    // Filter by type and specific item
    if (filterType === 'services' || selectedItem?.type === 'service') {
      orders = [];
      tickets = [];
      enrollments = [];
      if (selectedItem) {
        bookings = bookings.filter(b => b.service === selectedItem.name);
      }
    } else if (filterType === 'products' || selectedItem?.type === 'product') {
      bookings = [];
      tickets = [];
      enrollments = [];
      if (selectedItem) {
        orders = orders.filter(o => o.product === selectedItem.name);
      }
    } else if (filterType === 'events' || selectedItem?.type === 'event') {
      bookings = [];
      orders = [];
      enrollments = [];
      if (selectedItem) {
        tickets = tickets.filter(t => t.event === selectedItem.name);
      }
    } else if (filterType === 'courses' || selectedItem?.type === 'course') {
      bookings = [];
      orders = [];
      tickets = [];
      if (selectedItem) {
        enrollments = enrollments.filter(e => e.course === selectedItem.name);
      }
    }

    // Calculate totals
    const totalBookings = bookings.length;
    const totalOrders = orders.length;
    const totalTickets = tickets.reduce((acc, t) => acc + t.tickets, 0);
    const totalEnrollments = enrollments.length;

    const bookingsRevenue = bookings.reduce((acc, b) => acc + b.price, 0);
    const ordersRevenue = orders.reduce((acc, o) => acc + o.price, 0);
    const ticketsRevenue = tickets.reduce((acc, t) => acc + t.price, 0);
    const enrollmentsRevenue = enrollments.reduce((acc, e) => acc + e.price, 0);

    const totalRevenue = bookingsRevenue + ordersRevenue + ticketsRevenue + enrollmentsRevenue;
    const totalTransactions = totalBookings + totalOrders + totalTickets + totalEnrollments;

    // New clients (unique)
    const allClients = [
      ...bookings.map(b => b.client),
      ...orders.map(o => o.client),
      ...tickets.map(t => t.client),
      ...enrollments.map(e => e.client)
    ];
    const uniqueClients = [...new Set(allClients)].length;

    return {
      bookings,
      orders,
      tickets,
      enrollments,
      totals: {
        bookings: totalBookings,
        orders: totalOrders,
        tickets: totalTickets,
        enrollments: totalEnrollments,
        revenue: totalRevenue,
        transactions: totalTransactions,
        clients: uniqueClients,
        bookingsRevenue,
        ordersRevenue,
        ticketsRevenue,
        enrollmentsRevenue
      }
    };
  }, [filterType, selectedItem, officeStats]);

  // Top performing items
  const topItems = useMemo(() => {
    const serviceStats = {};
    officeStats.bookings.forEach(b => {
      if (!serviceStats[b.service]) serviceStats[b.service] = { name: b.service, count: 0, revenue: 0 };
      serviceStats[b.service].count++;
      serviceStats[b.service].revenue += b.price;
    });
    return Object.values(serviceStats).sort((a, b) => b.revenue - a.revenue).slice(0, 5);
  }, [officeStats]);

  const clearFilters = () => {
    setFilterType('all');
    setSelectedItem(null);
    setDateRange('week');
  };

  const getFilterLabel = () => {
    if (selectedItem) return selectedItem.name;
    if (filterType === 'services') return 'Tutti i servizi';
    if (filterType === 'products') return 'Tutti i prodotti';
    if (filterType === 'events') return 'Tutti gli eventi';
    if (filterType === 'courses') return 'Tutti i corsi';
    return 'Tutti gli elementi';
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 lg:pb-8">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 pt-6 pb-6">
        <div className="max-w-screen-2xl mx-auto px-6">
          {/* Top row: Title + Badge */}
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-emerald-100 text-sm">Bentornato</p>
              <h1 className="text-2xl lg:text-3xl font-bold text-white">{officeName}</h1>
            </div>
            
            {isElitPartner && (
              <div className="flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-1.5 rounded-full">
                <Crown className="w-4 h-4 text-white" />
                <span className="text-white text-sm font-medium">ELIT</span>
              </div>
            )}
          </div>
          
          {/* Office Switcher - Shows address */}
          {companyOffices && companyOffices.length > 1 ? (
            <div className="relative mb-4" ref={officeSwitcherRef}>
              <button
                onClick={() => setShowOfficeSwitcher(!showOfficeSwitcher)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-full text-white transition-all text-sm"
                data-testid="dashboard-office-switcher-btn"
              >
                <MapPin className="w-4 h-4 flex-shrink-0 text-emerald-200" />
                <span className="truncate max-w-[200px] lg:max-w-[400px]">{selectedOffice?.address || 'Seleziona sede'}</span>
                <span className="bg-white/25 px-1.5 py-0.5 rounded-full text-xs font-medium">{companyOffices.length}</span>
                <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${showOfficeSwitcher ? 'rotate-180' : ''}`} />
              </button>
              
              {showOfficeSwitcher && (
                <div className="absolute top-full left-0 mt-2 w-[calc(100vw-3rem)] max-w-md bg-white rounded-xl shadow-2xl border border-gray-100 z-50 py-2 animate-slide-down" data-testid="dashboard-office-switcher-dropdown">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                    Cambia sede
                  </div>
                  {companyOffices.map(office => (
                    <button
                      key={office.id}
                      onClick={() => { switchOffice(office.id); setShowOfficeSwitcher(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${office.id === selectedOffice?.id ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}
                      data-testid={`dashboard-office-switch-${office.id}`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${office.id === selectedOffice?.id ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate ${office.id === selectedOffice?.id ? 'text-emerald-700' : 'text-gray-900'}`}>{office.name}</p>
                        <p className="text-xs text-gray-500 truncate">{office.address}</p>
                      </div>
                      {office.id === selectedOffice?.id && (
                        <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Single office - just show address */
            <div className="flex items-center gap-2 text-emerald-200 text-sm mb-4">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{selectedOffice?.address}</span>
            </div>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div 
              data-testid="kpi-transactions"
              className="bg-white/20 backdrop-blur rounded-xl p-4 text-center"
            >
              <p className="text-3xl font-bold text-white">{filteredStats.totals.transactions}</p>
              <p className="text-emerald-100 text-sm">Transazioni</p>
            </div>
            <div 
              data-testid="kpi-revenue"
              className="bg-white/20 backdrop-blur rounded-xl p-4 text-center"
            >
              <p className="text-3xl font-bold text-white">{'\u20AC'}{filteredStats.totals.revenue.toFixed(0)}</p>
              <p className="text-emerald-100 text-sm">Incasso totale</p>
            </div>
            <div 
              data-testid="kpi-bookings"
              className="bg-white/20 backdrop-blur rounded-xl p-4 text-center cursor-pointer hover:bg-white/30 transition-colors"
              onClick={() => onNavigate && onNavigate('calendar')}
            >
              <p className="text-3xl font-bold text-white">{filteredStats.totals.bookings}</p>
              <p className="text-emerald-100 text-sm">Prenotazioni</p>
            </div>
            <div 
              data-testid="kpi-clients"
              className="bg-white/20 backdrop-blur rounded-xl p-4 text-center cursor-pointer hover:bg-white/30 transition-colors"
              onClick={() => onNavigate && onNavigate('clients')}
            >
              <p className="text-3xl font-bold text-white">{filteredStats.totals.clients}</p>
              <p className="text-emerald-100 text-sm">Clienti</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto px-6 py-3">
          <div className="flex items-center gap-2 overflow-x-auto">
            {/* Date Range Filter */}
            <button
              onClick={() => setShowDateFilter(!showDateFilter)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              <CalendarDays className="w-4 h-4" />
              {DATE_RANGES.find(d => d.id === dateRange)?.label}
              <ChevronDown className="w-4 h-4" />
            </button>

            {/* Item Type Filter */}
            <button
              onClick={() => setShowItemFilter(!showItemFilter)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                filterType !== 'all' || selectedItem 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="w-4 h-4" />
              {getFilterLabel()}
              <ChevronDown className="w-4 h-4" />
            </button>

            {/* Clear Filters */}
            {(filterType !== 'all' || selectedItem || dateRange !== 'week') && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
                Resetta
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Date Range Dropdown */}
      {showDateFilter && (
        <div className="fixed inset-0 z-30" onClick={() => setShowDateFilter(false)}>
          <div className="absolute top-[180px] left-4 lg:left-auto lg:ml-8 bg-white rounded-xl shadow-lg border border-gray-200 py-2 min-w-[200px]" onClick={e => e.stopPropagation()}>
            {DATE_RANGES.map(range => (
              <button
                key={range.id}
                onClick={() => { setDateRange(range.id); setShowDateFilter(false); }}
                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 ${dateRange === range.id ? 'text-emerald-600 font-medium bg-emerald-50' : 'text-gray-700'}`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Item Filter Dropdown */}
      {showItemFilter && (
        <div className="fixed inset-0 z-30" onClick={() => setShowItemFilter(false)}>
          <div className="absolute top-[180px] left-[150px] lg:left-auto lg:ml-[180px] bg-white rounded-xl shadow-lg border border-gray-200 py-2 min-w-[280px] max-h-[400px] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => { setFilterType('all'); setSelectedItem(null); setShowItemFilter(false); }}
              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 ${filterType === 'all' && !selectedItem ? 'text-emerald-600 font-medium bg-emerald-50' : 'text-gray-700'}`}
            >
              Tutti gli elementi
            </button>
            
            <div className="border-t border-gray-100 my-1" />
            
            {/* Services */}
            <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">Servizi</div>
            <button
              onClick={() => { setFilterType('services'); setSelectedItem(null); setShowItemFilter(false); }}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${filterType === 'services' && !selectedItem ? 'text-emerald-600 font-medium' : 'text-gray-700'}`}
            >
              Tutti i servizi
            </button>
            {partnerServices.map(service => (
              <button
                key={service.id}
                onClick={() => { setSelectedItem({ ...service, type: 'service' }); setFilterType('services'); setShowItemFilter(false); }}
                className={`w-full text-left px-6 py-2 text-sm hover:bg-gray-50 ${selectedItem?.id === service.id ? 'text-emerald-600 font-medium' : 'text-gray-600'}`}
              >
                {service.name}
              </button>
            ))}
            
            {/* Products */}
            <div className="border-t border-gray-100 my-1" />
            <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">Prodotti</div>
            <button
              onClick={() => { setFilterType('products'); setSelectedItem(null); setShowItemFilter(false); }}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${filterType === 'products' && !selectedItem ? 'text-emerald-600 font-medium' : 'text-gray-700'}`}
            >
              Tutti i prodotti
            </button>
            {partnerProducts.map(product => (
              <button
                key={product.id}
                onClick={() => { setSelectedItem({ ...product, type: 'product' }); setFilterType('products'); setShowItemFilter(false); }}
                className={`w-full text-left px-6 py-2 text-sm hover:bg-gray-50 ${selectedItem?.id === product.id ? 'text-emerald-600 font-medium' : 'text-gray-600'}`}
              >
                {product.name}
              </button>
            ))}
            
            {/* Events */}
            {partnerEvents.length > 0 && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">Eventi</div>
                <button
                  onClick={() => { setFilterType('events'); setSelectedItem(null); setShowItemFilter(false); }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${filterType === 'events' && !selectedItem ? 'text-emerald-600 font-medium' : 'text-gray-700'}`}
                >
                  Tutti gli eventi
                </button>
                {partnerEvents.map(event => (
                  <button
                    key={event.id}
                    onClick={() => { setSelectedItem({ ...event, type: 'event' }); setFilterType('events'); setShowItemFilter(false); }}
                    className={`w-full text-left px-6 py-2 text-sm hover:bg-gray-50 ${selectedItem?.id === event.id ? 'text-emerald-600 font-medium' : 'text-gray-600'}`}
                  >
                    {event.name}
                  </button>
                ))}
              </>
            )}
            
            {/* Courses */}
            {partnerCourses.length > 0 && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">Corsi</div>
                <button
                  onClick={() => { setFilterType('courses'); setSelectedItem(null); setShowItemFilter(false); }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${filterType === 'courses' && !selectedItem ? 'text-emerald-600 font-medium' : 'text-gray-700'}`}
                >
                  Tutti i corsi
                </button>
                {partnerCourses.map(course => (
                  <button
                    key={course.id}
                    onClick={() => { setSelectedItem({ ...course, type: 'course' }); setFilterType('courses'); setShowItemFilter(false); }}
                    className={`w-full text-left px-6 py-2 text-sm hover:bg-gray-50 ${selectedItem?.id === course.id ? 'text-emerald-600 font-medium' : 'text-gray-600'}`}
                  >
                    {course.name}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Main Content - Direct overview, no tabs */}
      <div className="max-w-screen-2xl mx-auto px-6 py-6">
        {/* Top row: Revenue + Chart side by side on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Revenue Breakdown */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Dettaglio Incassi</h3>
            {/* Empty state quando non ci sono dati */}
            {filteredStats.totals.revenue === 0 && partnerServices.length === 0 && partnerProducts.length === 0 && partnerEvents.length === 0 && partnerCourses.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">Nessun incasso registrato</p>
                <p className="text-gray-400 text-sm mt-1">Gli incassi appariranno qui quando riceverai prenotazioni o ordini</p>
                <button 
                  onClick={() => onNavigate('office-catalog')}
                  className="mt-4 text-emerald-600 text-sm font-medium hover:text-emerald-700"
                >
                  Configura il tuo catalogo →
                </button>
              </div>
            ) : filteredStats.totals.revenue === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">Nessuna vendita nel periodo selezionato</p>
                <p className="text-gray-400 text-sm mt-1">Prova a modificare i filtri per vedere altri dati</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {/* Servizi - mostra solo se abilitato */}
                {hasModule('appointments') && partnerServices.length > 0 && (
                  <div className="bg-emerald-50 rounded-xl p-3.5 cursor-pointer hover:bg-emerald-100 transition-colors" onClick={() => onNavigate('office-catalog')}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Clock className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs text-gray-600">Servizi Orari</span>
                    </div>
                    <p className="text-xl font-bold text-emerald-600">€{filteredStats.totals.bookingsRevenue}</p>
                    <p className="text-xs text-gray-500">{partnerServices.length} servizi • {filteredStats.totals.bookings} prenotazioni</p>
                  </div>
                )}
                {/* Noleggi - mostra solo se abilitato */}
                {hasModule('rentals') && partnerRentals.length > 0 && (
                  <div className="bg-sky-50 rounded-xl p-3.5 cursor-pointer hover:bg-sky-100 transition-colors" onClick={() => onNavigate('office-catalog')}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Key className="w-4 h-4 text-sky-600" />
                      <span className="text-xs text-gray-600">Noleggi</span>
                    </div>
                    <p className="text-xl font-bold text-sky-600">€{filteredStats.totals.rentalsRevenue || 0}</p>
                    <p className="text-xs text-gray-500">{partnerRentals.length} offerte</p>
                  </div>
                )}
                {/* Prodotti - mostra solo se abilitato */}
                {hasModule('ecommerce') && partnerProducts.length > 0 && (
                  <div className="bg-blue-50 rounded-xl p-3.5 cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => onNavigate('office-catalog')}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Package className="w-4 h-4 text-blue-600" />
                      <span className="text-xs text-gray-600">Prodotti</span>
                    </div>
                    <p className="text-xl font-bold text-blue-600">€{filteredStats.totals.ordersRevenue.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">{partnerProducts.length} prodotti • {filteredStats.totals.orders} ordini</p>
                  </div>
                )}
                {/* Eventi - mostra solo se abilitato */}
                {hasModule('events') && partnerEvents.length > 0 && (
                  <div className="bg-purple-50 rounded-xl p-3.5 cursor-pointer hover:bg-purple-100 transition-colors" onClick={() => onNavigate('office-catalog')}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Calendar className="w-4 h-4 text-purple-600" />
                      <span className="text-xs text-gray-600">Eventi</span>
                    </div>
                    <p className="text-xl font-bold text-purple-600">€{filteredStats.totals.ticketsRevenue}</p>
                    <p className="text-xs text-gray-500">{filteredStats.totals.tickets} biglietti</p>
                  </div>
                )}
                {/* Corsi - mostra solo se abilitato */}
                {hasModule('courses') && partnerCourses.length > 0 && (
                  <div className="bg-indigo-50 rounded-xl p-3.5 cursor-pointer hover:bg-indigo-100 transition-colors" onClick={() => onNavigate('office-catalog')}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <BookOpen className="w-4 h-4 text-indigo-600" />
                      <span className="text-xs text-gray-600">Corsi</span>
                    </div>
                    <p className="text-xl font-bold text-indigo-600">€{filteredStats.totals.enrollmentsRevenue}</p>
                    <p className="text-xs text-gray-500">{partnerCourses.length} corsi • {filteredStats.totals.enrollments} iscrizioni</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Chart */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Andamento Incassi</h3>
              <span className="flex items-center gap-1 text-emerald-500 text-sm font-medium">
                <ArrowUp className="w-4 h-4" />
                +15% vs periodo precedente
              </span>
            </div>
            <div className="h-40 bg-gradient-to-t from-emerald-50 to-transparent rounded-xl flex items-end justify-around gap-1.5 p-4">
              {[35, 50, 40, 65, 45, 80, 60, 70, 55, 90, 75, 85].map((h, i) => (
                <div 
                  key={i}
                  className="flex-1 bg-emerald-500 rounded-t transition-all hover:bg-emerald-600 cursor-pointer group relative"
                  style={{ height: `${h}%` }}
                >
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    €{Math.round(h * 10)}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2 px-2">
              <span>Gen</span><span>Feb</span><span>Mar</span><span>Apr</span><span>Mag</span><span>Giu</span>
              <span>Lug</span><span>Ago</span><span>Set</span><span>Ott</span><span>Nov</span><span>Dic</span>
            </div>
          </div>
        </div>

        {/* Top Items Grid - 2 columns on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Top Services */}
          {partnerServices.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Servizi Più Richiesti</h3>
                <button onClick={() => onNavigate('office-catalog')} className="text-emerald-600 text-xs font-medium flex items-center gap-1 hover:text-emerald-700">
                  Vedi tutti <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-2">
                {topItems.map((item, i) => (
                  <button 
                    key={i} 
                    onClick={() => onNavigate('service-detail', { id: partnerServices[i]?.id || 1 })}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-emerald-50 transition-colors text-left"
                    data-testid={`top-service-${i}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</span>
                      <span className="font-medium text-gray-900 text-sm">{item.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-600 text-sm">€{item.revenue}</p>
                      <p className="text-xs text-gray-500">{item.count} pren.</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Top Products */}
          {partnerProducts.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Prodotti Più Venduti</h3>
                <button onClick={() => onNavigate('office-catalog')} className="text-blue-600 text-xs font-medium flex items-center gap-1 hover:text-blue-700">
                  Vedi tutti <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-2">
                {partnerProducts.slice(0, 5).map((product, i) => {
                  const productOrders = officeStats.orders.filter(o => o.product === product.name);
                  const revenue = productOrders.reduce((sum, o) => sum + o.price, 0);
                  return (
                    <button 
                      key={product.id} 
                      onClick={() => onNavigate('product-detail', { id: product.id })}
                      className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors text-left"
                      data-testid={`top-product-${i}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-7 h-7 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</span>
                        <div>
                          <span className="font-medium text-gray-900 text-sm">{product.name}</span>
                          <p className="text-xs text-gray-500">€{product.price}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-blue-600 text-sm">€{revenue.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">{productOrders.length} ordini</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top Events */}
          {partnerEvents.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Eventi Più Richiesti</h3>
                <button onClick={() => onNavigate('office-catalog')} className="text-purple-600 text-xs font-medium flex items-center gap-1 hover:text-purple-700">
                  Vedi tutti <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-2">
                {partnerEvents.slice(0, 5).map((event, i) => (
                  <button 
                    key={event.id} 
                    onClick={() => onNavigate('event-detail', { id: event.id })}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-purple-50 transition-colors text-left"
                    data-testid={`top-event-${i}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</span>
                      <div>
                        <span className="font-medium text-gray-900 text-sm">{event.name}</span>
                        <p className="text-xs text-gray-500">{event.date} • €{event.price}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Top Courses */}
          {partnerCourses.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Corsi Più Richiesti</h3>
                <button onClick={() => onNavigate('office-catalog')} className="text-indigo-600 text-xs font-medium flex items-center gap-1 hover:text-indigo-700">
                  Vedi tutti <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-2">
                {partnerCourses.slice(0, 5).map((course, i) => (
                  <button 
                    key={course.id} 
                    onClick={() => onNavigate('course-detail', { id: course.id })}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-indigo-50 transition-colors text-left"
                    data-testid={`top-course-${i}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</span>
                      <div>
                        <span className="font-medium text-gray-900 text-sm">{course.name}</span>
                        <p className="text-xs text-gray-500">€{course.price}/mese</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Elit Upsell */}
        {!isElitPartner && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-5 text-white">
            <div className="flex items-center gap-3 mb-3">
              <Crown className="w-8 h-8" />
              <div>
                <h4 className="font-bold">Diventa Partner Elit</h4>
                <p className="text-sm text-amber-100">Aumenta la tua visibilità e attira più clienti</p>
              </div>
            </div>
            <button 
              onClick={() => onNavigate('elit')}
              className="w-full bg-white text-amber-600 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-amber-50 transition-colors"
            >
              Scopri Elit
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerDashboard;
